import React from "react";
import Layout from "../components/layout/Layout/Layout";
import Header from "../components/layout/Header/Header";
import ArticleHero from "../components/global/Articles/Hero";
import LatestArticles from "../components/global/Articles/collections/LatestArticles";
import CellarFavorites from "../components/global/Articles/collections/CellarFavorites";
import VinousFavorites from "../components/global/Articles/collections/VinousFavorites";
import VinousTable from "../components/global/Articles/collections/VinousTable";
import HomepageAuthors from "../components/global/Authors/collections/HomepageAuthors";
import {
  getLatestArticles,
  getVinousTableArticles,
  getCellarFavoritesArticles,
} from "../lib/articleRequests";
import { getHomepageNotices } from "../lib/bannerRequests";
import Banners from "../components/global/Banners/Banners";
import HomepageMultimedia from "../components/global/Multimedia/collections/HomepageMultimedia";
import { getVinousFavorites } from "../lib/wineRequests";
import { useRouter } from "next/router";
import { useToast } from "../context/toastContext";
import Head from "next/head";
import Loading from "../components/global/Loading";
import { useStripeSuccess } from "../hooks/useStripeSuccess";
import SearchBar from "../components/global/SearchBar/SearchBar";
import { withUserSSR } from "../utils/withUserSSR";

// Identity markup for the brand result. Organization is what Google uses to
// resolve "Vinous" as an entity (display name, logo, knowledge panel); WebSite
// names the site itself and points back at the publisher via @id.
//
// Deliberately no SearchAction: the sitelinks searchbox needs a single
// site-wide search URL, but SearchBar.tsx dispatches to /wines, /articles?term=
// or /multimedia?term= depending on mode, so any one of them would misstate
// what site search covers.
const HOME_JSON_LD = JSON.stringify(
  {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://vinous.com/#organization",
        name: "Vinous",
        alternateName: "Vinous Media",
        url: "https://vinous.com",
        logo: {
          "@type": "ImageObject",
          url: "https://vinous.com/img/vinous-logo.png",
          width: 636,
          height: 209,
        },
        sameAs: [
          "https://twitter.com/VinousMedia",
          "https://www.facebook.com/vinousmedia",
          "https://www.instagram.com/vinousmedia",
        ],
      },
      {
        "@type": "WebSite",
        "@id": "https://vinous.com/#website",
        name: "Vinous",
        url: "https://vinous.com",
        publisher: { "@id": "https://vinous.com/#organization" },
      },
    ],
  },
  null,
  2
);

const HomePage = ({
  latestArticles,
  vinousTableArticles,
  cellarFavoritesArticles,
  vinousFavorites,
  homepageNotices,
}) => {
  const router = useRouter();
  const toast = useToast(2000);
  const { signed_up } = router.query;
  const { isCompletingUser } = useStripeSuccess();

  if (signed_up === "") {
    toast("success", "Welcome. You have subscribed successfully.", null, () => {
      router.replace("/", undefined, { shallow: true });
    });
  }

  return (
    <Layout>
      <Head>
        <title>Antonio Galloni presents Vinous | Explore All Things Wine</title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: HOME_JSON_LD }}
          key="home-jsonld"
        />
      </Head>
      <Header user={false} />
      {isCompletingUser ? (
        <Loading isLoading={isCompletingUser}></Loading>
      ) : (
        <>
          <SearchBar className="pb-2 md:pb-4" hideResults={true} autoComplete={true}/>
          <div className="xl:flex xl:space-x-4">
            <div className="w-full xl:w-5/6">
              <ArticleHero article={latestArticles[0]} />
              <LatestArticles latestArticles={latestArticles} />
              <VinousTable vinousTableArticles={vinousTableArticles} />
              <VinousFavorites vinousFavorites={vinousFavorites} />
              <CellarFavorites
                cellarFavoritesArticles={cellarFavoritesArticles}
              />
              <HomepageMultimedia />
              <HomepageAuthors />
            </div>
            <div className="w-full xl:w-1/6">
              <Banners banners={homepageNotices} />
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export const getServerSideProps = withUserSSR(async (context) => {
  // Forward the visitor's session so subscribers get full tasting notes.
  const cookie = context.req.headers.cookie || "";
  //Replace with Promise.all for better performance
  const [responseLatestArticles, responseVinousTable, responseCellarFavorites, responseNotices, responseVinousFavorites] = await Promise.all([
    getLatestArticles(),
    getVinousTableArticles(),
    getCellarFavoritesArticles(),
    getHomepageNotices(),
    getVinousFavorites({ per_page: 3 }, cookie)
  ]);

  return {
    props: {
      latestArticles: responseLatestArticles.data,
      vinousTableArticles: responseVinousTable.data,
      cellarFavoritesArticles: responseCellarFavorites.data,
      vinousFavorites: responseVinousFavorites.data.favorites,
      homepageNotices: responseNotices.data.banners,
    },
  };
});

export default HomePage;
